import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, useForm } from "react-hook-form";
import { format } from "date-fns";
import Loading from "components/Loading";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import { useOrgAdminAccess } from "hooks/useOrgAdminAccess";
import {
  getCmrDataSharingAgreement,
  getIpaStatus,
  submitIpa,
} from "services/consentService";
import { storage } from "services/firebase";

const IpaSubmissionPage = () => {
  const { user, userData, organizationId } = useAuth();
  const { canAccess } = useOrgAdminAccess();
  const institutionId =
    userData?.institutionId || userData?.organizationId || organizationId;

  const [statusRow, setStatusRow] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      boardResolutionDate: "",
      boardResolutionSignatory: "",
      cmrAccepted: false,
      governanceRole: "CEO",
      governanceAttested: false,
    },
  });

  const boardFile = watch("boardFile");

  const load = useCallback(async () => {
    if (!institutionId) return;
    setLoading(true);
    try {
      const [status, cmrDoc] = await Promise.all([
        getIpaStatus(institutionId),
        getCmrDataSharingAgreement(),
      ]);
      setStatusRow(status);
      setAgreement(cmrDoc);
    } catch (err) {
      setErrorMessage(err?.message || "Failed to load IPA status.");
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => {
    load();
  }, [load]);

  const onSubmit = async (values) => {
    if (!institutionId || !user?.uid) return;
    setErrorMessage("");
    setSuccessMessage("");
    setUploading(true);
    try {
      if (!values.boardFile?.[0]) {
        throw new Error("Board resolution PDF is required.");
      }
      if (!values.cmrAccepted || !values.governanceAttested) {
        throw new Error("All attestations must be accepted.");
      }

      const file = values.boardFile[0];
      const path = `ipa-documents/${institutionId}/board-resolution-${Date.now()}.pdf`;
      const ref = storage.ref(path);
      await ref.put(file);
      const boardResolutionUrl = await ref.getDownloadURL();

      await submitIpa({
        institutionId,
        boardResolutionUrl,
        boardResolutionDate: values.boardResolutionDate,
        boardResolutionSignatory: values.boardResolutionSignatory,
        cmrAgreementVersion: agreement?.version || "unknown",
        clinicalGovernanceAttestedRole: values.governanceRole,
      });

      setSuccessMessage("IPA submitted for platform owner review.");
      await load();
    } catch (err) {
      setErrorMessage(err?.message || "Submission failed.");
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <Loading page />;

  if (!canAccess) {
    return (
      <Alert severity="warning">
        IPA submission is available to organization admins only.
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      <DashboardPageHeader
        title="Institutional Participation Agreement"
        subtitle="Gates the CareMap verified marker. All other products remain available without IPA approval."
      />

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1">Current status: {statusRow?.status || "none"}</Typography>
        {statusRow?.expiresAt ? (
          <Typography variant="body2" color="text.secondary">
            Approved — renews by {format(new Date(statusRow.expiresAt), "MMM d, yyyy")}
          </Typography>
        ) : null}
      </Paper>

      {errorMessage ? <Alert severity="error" onClose={() => setErrorMessage("")}>{errorMessage}</Alert> : null}
      {successMessage ? <Alert severity="success" onClose={() => setSuccessMessage("")}>{successMessage}</Alert> : null}

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack component="form" spacing={3} onSubmit={handleSubmit(onSubmit)}>
          <Typography variant="h6">Board resolution</Typography>
          <Controller
            name="boardFile"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <TextField
                {...field}
                type="file"
                inputProps={{ accept: "application/pdf" }}
                onChange={(event) => onChange(event.target.files)}
                helperText="Upload signed board resolution PDF"
              />
            )}
          />
          <Controller
            name="boardResolutionDate"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField {...field} type="date" label="Resolution date" InputLabelProps={{ shrink: true }} />
            )}
          />
          <Controller
            name="boardResolutionSignatory"
            control={control}
            rules={{ required: true }}
            render={({ field }) => <TextField {...field} label="Signatory name and title" />}
          />

          <Typography variant="h6">CMR data sharing agreement</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
            {agreement?.text || "Agreement text is not yet published."}
          </Typography>
          <Controller
            name="cmrAccepted"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                label="I confirm that our governing board accepts these CMR terms"
              />
            )}
          />

          <Typography variant="h6">Clinical governance attestation</Typography>
          <Controller
            name="governanceRole"
            control={control}
            render={({ field }) => (
              <TextField {...field} select label="Your role">
                {["CEO", "CMO", "CNO", "Other"].map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="governanceAttested"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                label="I attest that our institution has the clinical governance structure to support the FDE model"
              />
            )}
          />

          <LoadingButton type="submit" variant="contained" loading={uploading}>
            Submit for review
          </LoadingButton>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default IpaSubmissionPage;
